import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import dayjs from 'dayjs';

import * as approvalV2Api from '../../lib/api/approvalV2';
import ApprovalV2HistoryPanel from '../../components/approval/ApprovalV2HistoryPanel';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';

const DOC_TYPE_LABELS = { ORDER_BOOK: '수주품의서', HR_LEAVE: '휴가', TAX_BILL_ISSUE: '세금계산서 발행 요청' };
const DOC_STATUS_LABELS = {
  IN_PROGRESS: '진행중',
  APPROVED: '승인',
  REJECTED: '반려',
  RECALLED: '회수',
  CANCELED: '취소',
};
const DOC_STATUS_COLORS = {
  IN_PROGRESS: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  RECALLED: 'default',
  CANCELED: 'default',
};

/**
 * 결재 모니터링(V2) - 2026-10-20 결재 V2 이관 후속(4절 과제).
 *
 * 기존 "결재 모니터링" 화면(ApprovalMonitorListContainer)은 COM_APPR_DET(V1) 기반이라
 * 결재선 템플릿/전결로 상신된 V2 문서(ORDER_BOOK/HR_LEAVE)가 전혀 보이지 않는다
 * (KSM도 동일한 제약 - 결재 V2 이관 작업 문서 "미해결/알려진 제약" 참고, KSM에 V2
 * 모니터링 화면 자체가 없음을 확인). bito는 제출자 무관 전체 조회가 가능한 관리자 전용
 * 엔드포인트(GET .../documents, ApprovalV2Service.assertCanManageApprovalAuth로 가드)를
 * 추가하고, 이 화면에서 docType/docStatus로만 거를 수 있게 간단히 구성했다(요청대로
 * "Keep it simple" - 페이징/날짜범위/키워드 검색 없음, 서버에서 최근 500건만 내려줌).
 *
 * 관리 권한이 없으면(canManage=false) 상위(ApprovalMonitorListPage)에서 탭 자체를
 * 숨기므로, 이 컨테이너는 권한이 있는 사용자만 마운트된다.
 */
const ApprovalV2MonitorContainer = () => {
  const [docType, setDocType] = useState('');
  const [docStatus, setDocStatus] = useState('');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [detailByDoc, setDetailByDoc] = useState({});

  const load = () => {
    setLoading(true);
    approvalV2Api
      .getAllApprovalV2Documents({ docType, docStatus })
      .then((res) => setDocs(res?.data?.list || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, docStatus]);

  const toggleExpand = async (apprDocId) => {
    const next = { ...expanded, [apprDocId]: !expanded[apprDocId] };
    setExpanded(next);
    if (next[apprDocId] && !detailByDoc[apprDocId]) {
      const res = await approvalV2Api.getApprovalV2Document(apprDocId);
      setDetailByDoc((prev) => ({ ...prev, [apprDocId]: res?.data }));
    }
  };

  return (
    <>
      <BreadcrumbsContainer title="결재관리/결재 모니터링(V2)" />
      <Paper sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>문서 유형</InputLabel>
          <Select
            label="문서 유형"
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
          >
            <MenuItem value="">전체</MenuItem>
            {Object.entries(DOC_TYPE_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>결재 상태</InputLabel>
          <Select
            label="결재 상태"
            value={docStatus}
            onChange={(e) => setDocStatus(e.target.value)}
          >
            <MenuItem value="">전체</MenuItem>
            {Object.entries(DOC_STATUS_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack spacing={1}>
        {docs.map((doc) => (
          <Paper key={doc.apprDocId} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip size="small" label={DOC_TYPE_LABELS[doc.docType] || doc.docType} variant="outlined" />
              <Typography variant="subtitle2" sx={{ flex: 1 }}>
                {/* ORDER_BOOK은 docTitle 자체에 이미 "(품의서 번호)"가 들어있어([OrdersService]
                    buildOrderApprovalDocTitle), 뒤에 refDocNo를 무조건 또 붙이면 "...
                    (OD_2026_10_005) (OD_2026_10_005)"처럼 중복 표시된다 - docTitle에 이미
                    포함돼 있으면 생략 (2026-10-20 화면 점검). */}
                {doc.refDocNo && doc.docTitle.includes(doc.refDocNo)
                  ? doc.docTitle
                  : `${doc.docTitle} (${doc.refDocNo || doc.refDocId})`}
              </Typography>
              <Chip
                size="small"
                label={DOC_STATUS_LABELS[doc.docStatus] || doc.docStatus}
                color={DOC_STATUS_COLORS[doc.docStatus] || 'default'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                상신 {doc.submittedByNm || '-'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 90 }}>
                {doc.docStatus === 'IN_PROGRESS' ? `현재 ${doc.currentActorNm || '-'}` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {doc.submittedAt ? dayjs(doc.submittedAt).format('YYYY-MM-DD HH:mm') : ''}
              </Typography>
              <IconButton size="small" onClick={() => toggleExpand(doc.apprDocId)}>
                {expanded[doc.apprDocId] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>

            <Collapse in={Boolean(expanded[doc.apprDocId])} unmountOnExit>
              <Box sx={{ mt: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                {detailByDoc[doc.apprDocId] ? (
                  <ApprovalV2HistoryPanel
                    steps={detailByDoc[doc.apprDocId].steps || []}
                    actions={detailByDoc[doc.apprDocId].actions || []}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    불러오는 중...
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Paper>
        ))}
        {!loading && docs.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            조건에 맞는 결재(V2) 문서가 없습니다.
          </Typography>
        )}
      </Stack>
      </Paper>
    </>
  );
};

export default ApprovalV2MonitorContainer;
